import Calls from './Calls';
import { useState, useEffect } from 'react';
import initialCalls from './calls_20.json';
import metadata from './call_metadata.json';

function App() {
  const [calls, setCalls] = useState(initialCalls);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('deadline');
  const callsPerPage = 20;
  const [activeFilters, setActiveFilters] = useState([]);
  const [isInitialState, setIsInitialState] = useState(true);
  const [initialPage, setInitialPage] = useState(1);

  // Get initial page from URL params first
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = parseInt(params.get('page'));
    if (pageParam && !isNaN(pageParam)) {
      setInitialPage(pageParam);
      setCurrentPage(pageParam);
    }
  }, []);

  // Load full calls data
  useEffect(() => {
    fetch('/calls.json')
      .then(response => response.json())
      .then(data => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const futureCalls = data.filter(call => {
          const deadline = new Date(call.entry_deadline);
          deadline.setHours(0, 0, 0, 0);
          return deadline >= today;
        });
        
        setCalls(futureCalls);
        setIsInitialState(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const sortCalls = (calls) => {
    return [...calls].sort((a, b) => {
      switch (sortOrder) {
        case 'deadline':
          return new Date(a.entry_deadline) - new Date(b.entry_deadline);
        case 'price':
          return a.entry_fee_order_index - b.entry_fee_order_index;
        case 'newest':
          return a.created_at_order_index - b.created_at_order_index;
        default:
          return 0;
      }
    });
  };

  const freeCalls = calls.filter(call => call.entry_fee === 0).length;
  const callTypes = calls.reduce((acc, call) => {
    if (call.call_type?.name) {
      acc[call.call_type.name] = (acc[call.call_type.name] || 0) + 1;
    }
    return acc;
  }, {});

  const toggleFilter = (filterType) => {
    setActiveFilters(prev => {
      const isActive = prev.includes(filterType);
      if (isActive) {
        return prev.filter(f => f !== filterType);
      } else {
        return [...prev, filterType];
      }
    });
    setCurrentPage(1);
    // Remove page from URL
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('page');
    window.history.pushState({}, '', newUrl);
  };

  // Update search handler
  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('page');
    window.history.pushState({}, '', newUrl);
  };

  // Update sort handler
  const handleSort = (e) => {
    setSortOrder(e.target.value);
    setCurrentPage(1);
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('page');
    window.history.pushState({}, '', newUrl);
  };

  const filteredCalls = sortCalls(calls.filter(call => {
    const searchRegex = new RegExp(searchText, 'i');
    const matchesSearch = searchText === '' || 
                         searchRegex.test(call.name) || 
                         searchRegex.test(call.description);
    
    // Separate free filter from call type filters
    const callTypeFilters = activeFilters.filter(f => f !== 'free');
    const hasFreeFilter = activeFilters.includes('free');
    
    // Check if matches free filter (if active)
    const matchesFreeFilter = !hasFreeFilter || call.entry_fee === 0;
    
    // Check if matches any call type filter (OR logic)
    const matchesCallTypes = callTypeFilters.length === 0 || 
                            callTypeFilters.some(filter => call.call_type?.name === filter);

    return matchesSearch && matchesFreeFilter && matchesCallTypes;
  }));

  const indexOfLastCall = currentPage * callsPerPage;
  const indexOfFirstCall = indexOfLastCall - callsPerPage;
  const currentCalls = filteredCalls.slice(indexOfFirstCall, indexOfLastCall);
  const totalPages = Math.ceil(filteredCalls.length / callsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Update URL without reload
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('page', pageNumber);
    window.history.pushState({}, '', newUrl);
    // Scroll to top instantly
    window.scrollTo(0, 0);
  };

  const resetFilters = () => {
    setSearchText('');
    setSortOrder('deadline');
    setCurrentPage(1);
    setActiveFilters([]);
    // Remove page from URL
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('page');
    window.history.pushState({}, '', newUrl);
  };

  // Calculate which calls to show based on initial or full state
  const getDisplayCalls = () => {
    if (isInitialState) {
      if (initialPage === 1) {
        return initialCalls;
      } else {
        // Show loading state if trying to access other pages before data loads
        return [];
      }
    } else {
      return currentCalls;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="bg-white p-4 shadow-lg">
        <div className="flex justify-between items-center">
          <a href="/" className="text-xl md:text-2xl font-bold text-black hover:text-gray-800">
            <img src="/logo192.png" alt="Art Call Opportunities" className="h-8" />
          </a>
        </div>

        <div className="container mx-auto">
          <div className="mt-4 flex flex-wrap gap-1 sm:gap-2 items-center">
            <button
              onClick={() => toggleFilter('free')}
              disabled={isInitialState}
              className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium cursor-pointer transition-colors border border-black ${isInitialState ? 'opacity-50 cursor-not-allowed' : ''}
                ${activeFilters.includes('free') 
                  ? 'bg-yellow-200 text-black hover:bg-yellow-300' 
                  : 'bg-white text-black hover:bg-gray-100'}`}
            >
              {freeCalls} Free Calls 🎉
            </button>
            {Object.entries(callTypes).sort((a, b) => a[0].length - b[0].length).map(([type, count]) => (
              <button
                key={type}
                onClick={() => toggleFilter(type)}
                disabled={isInitialState}
                className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium cursor-pointer transition-colors border border-black ${isInitialState ? 'opacity-50 cursor-not-allowed' : ''}
                  ${activeFilters.includes(type)
                    ? 'bg-yellow-200 text-black hover:bg-yellow-300'
                    : 'bg-white text-black hover:bg-gray-100'}`}
              >
                {count} {type}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <input
              type="text"
              placeholder="Search calls..."
              value={searchText}
              onChange={handleSearch}
              disabled={isInitialState}
              autoFocus
              className={`w-full p-2 border-b-2 border-black focus:outline-none transition-all bg-transparent text-lg ${isInitialState ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
            <div className="flex mt-4 justify-between items-center mt-2 text-gray-600 text-sm">
              <div>
                {filteredCalls.length === 0 ? (
                  <span>0 calls</span>
                ) : (
                  <>
                    {((currentPage - 1) * 20) + 1}-{Math.min(currentPage * 20, filteredCalls.length)} of{' '}
                    <span className="font-bold">
                      {isInitialState ? metadata.total_calls : filteredCalls.length} calls
                    </span>
                  </>
                )}
              </div>
              <select
                value={sortOrder}
                onChange={handleSort}
                disabled={isInitialState}
                className={`px-1 py-1 rounded-lg border border-black text-sm font-medium cursor-pointer hover:bg-yellow-300 transition-colors ${isInitialState ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="deadline">Sort: Deadline (soonest)</option>
                <option value="price">Sort: Price (cheapest)</option>
                <option value="newest">Sort: Newest</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-2 sm:px-4 max-w-6xl">
        {(isInitialState && initialPage !== 1) ? (
          <div className="flex justify-center items-center min-h-screen">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {filteredCalls.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
                <p className="text-lg text-gray-600">No calls found matching your criteria</p>
                {(activeFilters.length > 0 || searchText || currentPage > 1) && (
                  <button
                    onClick={resetFilters}
                    className="px-3 py-1.5 border border-black text-sm font-medium text-black hover:bg-gray-100 bg-red-300 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reset
                  </button>
                )}
              </div>
            ) : (
              <>
                {(activeFilters.length > 0 || searchText || currentPage > 1) && (
                  <div className="flex justify-end py-2 sm:py-4">
                    <button
                      onClick={resetFilters}
                      className="px-2 py-1 sm:px-3 sm:py-1.5 border border-black text-xs sm:text-sm font-medium text-black hover:bg-gray-100 bg-red-300 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Reset
                    </button>
                  </div>
                )}
                <Calls calls={getDisplayCalls()} searchText={searchText} />
                {!isInitialState && totalPages > 1 && (
                  <div className="flex flex-wrap justify-center items-center gap-1 my-4 sm:my-8">
                    <button 
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 sm:px-3 sm:py-2 rounded-lg bg-white border border-black text-xs sm:text-sm font-medium text-black hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      First
                    </button>
                    <button 
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 sm:px-3 sm:py-2 rounded-lg bg-white border border-black text-xs sm:text-sm font-medium text-black hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      Prev
                    </button>

                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(3, totalPages))].map((_, idx) => {
                        let pageNum;
                        if (totalPages <= 3) {
                          pageNum = idx + 1;
                        } else if (currentPage <= 2) {
                          pageNum = idx + 1;
                        } else if (currentPage >= totalPages - 1) {
                          pageNum = totalPages - 2 + idx;
                        } else {
                          pageNum = currentPage - 1 + idx;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`px-2 py-1 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-medium cursor-pointer border border-black
                              ${currentPage === pageNum 
                                ? 'bg-black text-white' 
                                : 'bg-white text-black hover:bg-gray-100'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button 
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 sm:px-3 sm:py-2 rounded-lg bg-white border border-black text-xs sm:text-sm font-medium text-black hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      Next
                    </button>
                    <button 
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 sm:px-3 sm:py-2 rounded-lg bg-white border border-black text-xs sm:text-sm font-medium text-black hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      Last
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
